import type { SongLine, Chord } from '../../types';

/**
 * A display group: one navigable lyric/section/mixed line with any
 * preceding chord lines attached above it.
 */
export interface LineGroup {
  /** Chord text to show above the lyric (merged from preceding chord lines). */
  chordText: string | null;
  /** Structured chord data from preceding chord-only lines, with positions preserved. */
  chords: Chord[] | null;
  /** The primary line (lyric, mixed, or section). */
  line: SongLine;
}

/**
 * Returns true if a group has actual lyric or chord content worth
 * stopping on during auto-scroll / manual navigation.  Section headers
 * and blank spacer lines are skipped.
 */
export function isPromptableGroup(group: LineGroup): boolean {
  // Has chord content above the line
  if (group.chordText && group.chordText.trim().length > 0) return true;
  // Section header — skip
  const text = group.line.text.trim();
  if (group.line.type === 'section' || /^\[.+\]$/.test(text)) return false;
  // Non-empty lyric text
  if (text.length > 0) return true;
  return false;
}

/** Find the next promptable group index (forward). Returns current if none found. */
export function nextPromptableIndex(groups: LineGroup[], current: number): number {
  for (let i = current + 1; i < groups.length; i++) {
    if (isPromptableGroup(groups[i])) return i;
  }
  return current;
}

/** Find the previous promptable group index (backward). Returns current if none found. */
export function prevPromptableIndex(groups: LineGroup[], current: number): number {
  for (let i = current - 1; i >= 0; i--) {
    if (isPromptableGroup(groups[i])) return i;
  }
  return current;
}

/** Find the nearest promptable group index at or after the given index. */
export function nearestPromptableIndex(groups: LineGroup[], idx: number): number {
  // Search forward first
  for (let i = idx; i < groups.length; i++) {
    if (isPromptableGroup(groups[i])) return i;
  }
  // Then backward
  for (let i = idx - 1; i >= 0; i--) {
    if (isPromptableGroup(groups[i])) return i;
  }
  return idx;
}
